import React, { useState, useEffect, useCallback } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
	Box,
	Typography,
	Button,
	IconButton,
	Chip,
	LinearProgress,
} from "@mui/material";
import {
	Close as CloseIcon,
	PlayArrow as PlayArrowIcon,
	Pause as PauseIcon,
	SkipPrevious as SkipPreviousIcon,
	FilterList as FilterListIcon,
	Search as SearchIcon,
	Timeline as TimelineIcon,
	TableChart as TableChartIcon,
	Comment as CommentIcon,
	Speed as SpeedIcon,
	ToggleOn as ToggleOnIcon,
	Person as PersonIcon,
	Info as InfoIcon,
} from "@mui/icons-material";

// Clicks a tour-controlled element (e.g. a modal/panel close button) if it is
// present in the DOM. Used to auto-close things the user opened during the tour.
const closeTourModal = (selector) => {
	try {
		const el = document.querySelector(selector);
		if (el) el.click();
	} catch (e) {
		// no-op — the element may not be rendered
	}
};

const SerieTrackingJoyrideTour = ({ isOpen, onClose }) => {
	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	// Tour steps for Series Tracking page
	const steps = [
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<TableChartIcon sx={{ color: "#cc0000" }} />
						Series Tracking Tour
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						Welcome to the Series Tracking module - your tool for monitoring
						series-production part status, milestones, and follow-ups across the
						MAN build lifecycle.
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
						<Chip
							icon={<FilterListIcon />}
							label="Advanced Filtering"
							size="small"
							sx={{ bgcolor: "#e3f2fd" }}
						/>
						<Chip
							icon={<SearchIcon />}
							label="Smart Search"
							size="small"
							sx={{ bgcolor: "#e8f5e8" }}
						/>
						<Chip
							icon={<TimelineIcon />}
							label="Milestones"
							size="small"
							sx={{ bgcolor: "#fff3e0" }}
						/>
						<Chip
							icon={<CommentIcon />}
							label="Collaboration"
							size="small"
							sx={{ bgcolor: "#fce4ec" }}
						/>
					</Box>
					<Typography
						variant="body2"
						sx={{ color: "#666", fontStyle: "italic" }}
					>
						This tour will guide you through all key features in about 2
						minutes.
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 10000,
				},
			},
		},
		{
			target: '[data-tour-target="serie-sidebar"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 2, color: "#1e2832" }}
					>
						Series Tracking Filters Sidebar
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The sidebar provides comprehensive filtering for series data. Filter
						by projects, ALG, programm, change numbers, SNR, suppliers,
						responsible person, SE team, Emcos milestones, and date ranges.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							Series-Specific Filters:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• Project & change-number filtering • Emcos milestone tracking •
							Responsible person filtering • Special-release date selections
						</Typography>
					</Box>
				</Box>
			),
			placement: "right",
			spotlightPadding: 10,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="additional-filters-toggle"]',
			hideNext: true,
			spotlightClicks: true,
			disableBeacon: true,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<FilterListIcon sx={{ color: "#cc0000" }} />
						Additional Filters
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Beyond the main filters, there's a whole set of advanced filters.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00", display: "block" }}
						>
							👉 Click the "Additional Filters" button to continue
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							The tour will move on automatically once the panel opens.
						</Typography>
					</Box>
				</Box>
			),
			placement: "right",
			spotlightPadding: 6,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="additional-filters-modal"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<FilterListIcon sx={{ color: "#cc0000" }} />
						Additional Filters Panel
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This panel holds the advanced filtering options. Combine them with
						the main sidebar filters to precisely narrow down the series
						tracking data you need.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							Advanced Options:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• BTT status & extra category filters • Milestone-level selections
							• Fine-grained date ranges
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1.5, color: "#666", fontStyle: "italic" }}
					>
						Click Next to continue — we'll close this panel for you.
					</Typography>
				</Box>
			),
			placement: "center",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-search-bar"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SearchIcon sx={{ color: "#cc0000" }} />
						Search Bar
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Search across Series Tracking data by part number (SNR) or
						description. Results filter in real time.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e8f5e8",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #4caf50",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#2e7d32" }}
						>
							Search Capabilities:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#2e7d32" }}
						>
							• SNR (part numbers) • Descriptions • Instant results
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom-start",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-milestone-row"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<TimelineIcon sx={{ color: "#cc0000" }} />
						Milestone Row
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Each record shows a milestone timeline that runs from FET through to
						PET. The colored segments visualize the milestone progression (A80,
						E99, P99 and more) so you can see status at a glance.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00" }}
						>
							Milestone Timeline:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							• Runs from FET to PET • Colored progression segments • Status per
							milestone at a glance
						</Typography>
					</Box>
				</Box>
			),
			placement: "top",
			spotlightPadding: 6,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-quality-modal"]',
			hideNext: true,
			spotlightClicks: true,
			disableBeacon: true,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<TimelineIcon sx={{ color: "#cc0000" }} />
						Milestone Detail Button
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This button opens the detailed milestone view, showing all milestone
						dates, quality statuses, and the full breakdown for that part.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00", display: "block" }}
						>
							👉 Click the milestone detail button to continue
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							The tour will move on automatically once the milestone view
							opens.
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-quality-modal-content"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<TimelineIcon sx={{ color: "#cc0000" }} />
						Milestone Detail View
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This view shows the complete milestone breakdown for the record —
						every milestone date, quality status, and the VDAT values you can
						edit inline.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Detailed View:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• All milestone dates • Quality statuses • Editable VDAT values
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1.5, color: "#666", fontStyle: "italic" }}
					>
						Click Next to continue — we'll close this view for you.
					</Typography>
				</Box>
			),
			placement: "center",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-more-info"]',
			hideNext: true,
			spotlightClicks: true,
			disableBeacon: true,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<InfoIcon sx={{ color: "#cc0000" }} />
						More Info Button
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The "More info" button opens a side panel with additional details
						about the part.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00", display: "block" }}
						>
							👉 Click the "More info" button to continue
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							The tour will move on automatically once the panel opens.
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-sidepanel"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<InfoIcon sx={{ color: "#cc0000" }} />
						More Info Panel
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This side panel shows extended details for the selected series
						record, including technical specifications, metadata, and tags.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Expanded Information:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Extended part details • Technical specifications • Additional
							metadata
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1.5, color: "#666", fontStyle: "italic" }}
					>
						Click Next to continue — we'll close this panel for you.
					</Typography>
				</Box>
			),
			placement: "left",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-comment-button"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<CommentIcon sx={{ color: "#cc0000" }} />
						Comments
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use the comment button to add and view comments for a series record.
						Enable team collaboration with context-specific discussions and
						comment history.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f1f8e9",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #689f38",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#558b2f" }}
						>
							Comment System:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#558b2f" }}
						>
							• Series-specific comments • Team collaboration • Comment history
							tracking
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-resp-field"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<PersonIcon sx={{ color: "#cc0000" }} />
						Responsible Field
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The "Resp" field shows the person responsible for the record. In
						edit mode you can assign or update the responsible person, and
						changes are saved to the series record.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Responsibility Tracking:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Assign responsible person • Update via edit mode • Track
							ownership per record
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="serie-status-toggle"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<ToggleOnIcon sx={{ color: "#cc0000" }} />
						Status Toggle Switch
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The status toggle cycles through three states: None → in Arbeit →
						erledigt. Click to update the working status of a record with
						immediate visual feedback.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00" }}
						>
							3-State Toggle System:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							• None (Grey) - Not started • in Arbeit (Orange) - Work ongoing •
							erledigt (Green) - Completed • Click to cycle states
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SpeedIcon sx={{ color: "#4caf50" }} />
						Series Tracking Mastery!
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						You're now equipped to effectively use the Series Tracking module for
						monitoring series-production part status and milestones.
					</Typography>

					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 1.5, color: "#cc0000" }}
					>
						Key Series Tracking Workflows:
					</Typography>
					<Box sx={{ mb: 2 }}>
						{[
							"Filter by project or change number first",
							"Open milestone details for a record",
							"Use comments to collaborate",
							"Assign the responsible person",
							"Monitor 3-state status progression",
						].map((tip, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{
									display: "flex",
									alignItems: "center",
									mb: 0.5,
									color: "#555",
								}}
							>
								<Box
									sx={{
										width: 6,
										height: 6,
										bgcolor: "#cc0000",
										borderRadius: "50%",
										mr: 1,
									}}
								/>
								{tip}
							</Typography>
						))}
					</Box>

					<Typography
						variant="body2"
						sx={{
							color: "#666",
							fontStyle: "MAN Europe_Bold",
							textAlign: "center",
							mt: 2,
						}}
					>
						Happy Tracking! 🚚
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
		},
	];

	useEffect(() => {
		if (isOpen) {
			setRun(true);
			setStepIndex(0);
			setIsPlaying(true);
		} else {
			setRun(false);
		}
	}, [isOpen]);

	// On forced "click to open" steps, advance the tour only once the user
	// actually clicks the button (which opens the panel/modal).
	useEffect(() => {
		if (!run) return undefined;
		const currentTarget = steps[stepIndex]?.target;
		const forcedClickTargets = [
			'[data-tour-target="additional-filters-toggle"]',
			'[data-tour-target="serie-quality-modal"]',
			'[data-tour-target="serie-more-info"]',
		];
		if (!forcedClickTargets.includes(currentTarget)) {
			return undefined;
		}
		const btn = document.querySelector(currentTarget);
		if (!btn) return undefined;
		const handler = () => {
			// Give the panel/modal a moment to open before highlighting it.
			setTimeout(() => setStepIndex((i) => i + 1), 350);
		};
		btn.addEventListener("click", handler);
		return () => btn.removeEventListener("click", handler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepIndex, run]);

	const closeTourExtras = () => {
		// Close anything the user may have opened during the tour.
		closeTourModal('[data-tour-target="additional-filters-close"]');
		closeTourModal('[data-tour-target="serie-quality-modal-close"]');
		closeTourModal('[data-tour-target="serie-sidepanel-close"]');
	};

	const handleJoyrideCallback = useCallback(
		(data) => {
			const { action, index, status, type, step } = data;

			if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
				// Auto-close panels/modals the user was asked to open on the step
				// we're leaving.
				if (step?.target === '[data-tour-target="additional-filters-modal"]') {
					closeTourModal('[data-tour-target="additional-filters-close"]');
				}
				if (
					step?.target === '[data-tour-target="serie-quality-modal-content"]'
				) {
					closeTourModal('[data-tour-target="serie-quality-modal-close"]');
				}
				if (step?.target === '[data-tour-target="serie-sidepanel"]') {
					closeTourModal('[data-tour-target="serie-sidepanel-close"]');
				}
				const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
				setStepIndex(nextStepIndex);
			} else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
				closeTourExtras();
				setRun(false);
				onClose();
			}
		},
		[onClose],
	);

	const handleSkipTour = () => {
		// When user clicks "Skip Tour" or the close (X) button, skip the entire tour
		closeTourExtras();
		setRun(false);
		onClose();
	};

	const handlePlay = () => {
		setIsPlaying(!isPlaying);
		setRun(!isPlaying);
	};

	const progress = ((stepIndex + 1) / steps.length) * 100;

	// Custom tooltip component
	const CustomTooltip = ({
		index,
		step,
		backProps,
		primaryProps,
		tooltipProps,
	}) => (
		<Box
			{...tooltipProps}
			sx={{
				backgroundColor: "white",
				borderRadius: "12px",
				boxShadow: "0 8px 32px rgba(30, 40, 50, 0.15)",
				maxWidth: step.content.props.sx?.maxWidth || 400,
				overflow: "hidden",
				fontFamily: "MAN EUROPE_Regular",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					p: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						fontSize: "14px",
						fontFamily: "MAN EUROPE_Bold",
					}}
				>
					Series Tracking Tour ({index + 1}/{steps.length})
				</Typography>
				<Box sx={{ display: "flex", gap: 1 }}>
					<IconButton
						{...backProps}
						size="small"
						disabled={index === 0}
						sx={{ color: index === 0 ? "#666" : "white" }}
					>
						<SkipPreviousIcon fontSize="small" />
					</IconButton>
					<IconButton onClick={handlePlay} size="small" sx={{ color: "white" }}>
						{isPlaying ? (
							<PauseIcon fontSize="small" />
						) : (
							<PlayArrowIcon fontSize="small" />
						)}
					</IconButton>
					<IconButton
						onClick={handleSkipTour}
						size="small"
						sx={{ color: "white" }}
						title="Skip Tour"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			{/* Progress Bar */}
			<Box sx={{ px: 2, pt: 1 }}>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{
						height: 4,
						borderRadius: 2,
						backgroundColor: "#e0e0e0",
						"& .MuiLinearProgress-bar": {
							backgroundColor: "#cc0000",
						},
					}}
				/>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						textAlign: "center",
						mt: 0.5,
						color: "#666",
						fontSize: "12px",
					}}
				>
					{Math.round(progress)}% Complete
				</Typography>
			</Box>
			{/* Content */}
			<Box sx={{ p: 0 }}>{step.content}</Box>
			{/* Footer */}
			<Box
				sx={{
					p: 2,
					borderTop: "1px solid #eee",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "#f8f9fa",
				}}
			>
				<Button
					onClick={handleSkipTour}
					variant="text"
					sx={{
						color: "#666",
						fontSize: "12px",
						fontFamily: "MAN EUROPE_Regular",
					}}
				>
					Skip Tour
				</Button>
				<Box sx={{ display: "flex", gap: 1 }}>
					{index > 0 && (
						<Button
							{...backProps}
							variant="outlined"
							size="small"
							sx={{
								borderColor: "#cc0000",
								color: "#cc0000",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									borderColor: "#aa0000",
									backgroundColor: "#ffebee",
								},
							}}
						>
							Previous
						</Button>
					)}
					{step.hideNext ? (
						<Typography
							variant="caption"
							sx={{
								color: "#cc0000",
								fontFamily: "MAN EUROPE_Bold",
								fontSize: "12px",
								alignSelf: "center",
								paddingLeft:"10px"
							}}
						>
							Click highlighted button to continue
						</Typography>
					) : (
						<Button
							{...primaryProps}
							variant="contained"
							size="small"
							sx={{
								backgroundColor: "#cc0000",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									backgroundColor: "#aa0000",
								},
							}}
						>
							{index === steps.length - 1 ? "Finish" : "Next"}
						</Button>
					)}
				</Box>
			</Box>
		</Box>
	);

	return (
		<>
			{/* Custom CSS: highlight target by brightening it and dimming the rest */}
			<style>{`
        .__floater__arrow polygon {
          fill: #fff !important;
        }

        .react-joyride__spotlight {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          mix-blend-mode: normal !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4),
            0 0 16px 3px rgba(255, 255, 255, 0.4) !important;
        }

        .react-joyride__overlay {
          mix-blend-mode: normal !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
        }

        .react-joyride__spotlight {
          z-index: 9998 !important;
        }

        .react-joyride__tooltip {
          z-index: 9999 !important;
        }
      `}</style>
			<Joyride
				callback={handleJoyrideCallback}
				continuous={true}
				run={run && isPlaying}
				scrollToFirstStep={true}
				showProgress={false}
				showSkipButton={true}
				stepIndex={stepIndex}
				steps={steps}
				tooltipComponent={CustomTooltip}
				disableScrolling={true}
				spotlightClicks={true}
				disableOverlayClose={true}
				styles={{
					options: {
						arrowColor: "#fff",
						backgroundColor: "#fff",
						overlayColor: "rgba(0, 0, 0, 0.4)",
						primaryColor: "#cc0000",
						textColor: "#333",
						width: undefined,
						zIndex: 10000,
					},
					overlay: {
						mixBlendMode: "normal",
						backgroundColor: "rgba(0, 0, 0, 0.4)",
					},
					spotlight: {
						borderRadius: "8px",
						border: "none",
						backgroundColor: "transparent",
						boxShadow:
							"0 0 0 9999px rgba(0, 0, 0, 0.4), 0 0 16px 3px rgba(255, 255, 255, 0.4)",
						filter: "none !important",
						opacity: 1,
						mixBlendMode: "normal",
					},
					spotlightLegacy: {
						borderRadius: "8px",
						backgroundColor: "transparent",
						filter: "none",
					},
				}}
				locale={{
					back: "Previous",
					close: "Close",
					last: "Finish",
					next: "Next",
					skip: "Skip Tour",
				}}
			/>
		</>
	);
};

export default SerieTrackingJoyrideTour;
